import { createSelector,createSlice, PayloadAction, SerializedError } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import { StepFunctionExecution, StepFunctionExecutionDetails } from './AccountLinkingDashboardTypeDefs';

export interface AccountLinkingDashboardState {
    rows?: StepFunctionExecution[];
    rowDetails?: Array<StepFunctionExecutionDetails>;
    page: number;
    error?: SerializedError;
}

type StateWithAccountLinkingDashboard = { accountLinkingDashboard: AccountLinkingDashboardState };

const initialState: AccountLinkingDashboardState = {
    rows: [],
    rowDetails: [],
    page: 0
};

const accountLinkingDashboardSlice = createSlice({
    name: 'accountLinkingDashboard',
    initialState,

    reducers: (create) => ({
        setRows: create.reducer((state, action: PayloadAction<StepFunctionExecution[]>) => {
            state.rows = action.payload;
        }),

        addRowDetails: create.reducer((state, action: PayloadAction<StepFunctionExecutionDetails>) => {
            state.rowDetails?.push(action.payload);
        }),

        updateRowDetailsStatus: create.reducer((state, action: PayloadAction<[string | undefined, string]>) => {
            const [executionId, status] = action.payload;
            const row = state.rowDetails?.find((x) => x.executionId == executionId);
            if (row) {
                state.rowDetails?.splice(state.rowDetails.indexOf(row), 1, { ...row, status: status });
            }
        }),

        removeRow: create.reducer((state, action: PayloadAction<string>) => {
            const row = state.rows?.find((x) => x.executionId == action.payload);
            if (row) {
                state.rows?.splice(state.rows.indexOf(row), 1);
            }
        }),

        setPage: create.reducer((state, action: PayloadAction<number>) => {
            state.page = action.payload;
        }),

        clearState: create.reducer(() => initialState)
    })
});

// eslint-disable-next-line no-restricted-syntax
export default accountLinkingDashboardSlice.reducer;
export const { setRows, addRowDetails, updateRowDetailsStatus, removeRow, setPage, clearState } = accountLinkingDashboardSlice.actions;
export const selectPage = (state: StateWithAccountLinkingDashboard) => state.accountLinkingDashboard.page;

// Base selector for rows
const selectRows = (state: StateWithAccountLinkingDashboard) => state.accountLinkingDashboard.rows;

// Memoized selector for sorted rows
export const selectRowsByDateDescending = createSelector(
    [selectRows],
    (rows): StepFunctionExecution[] | undefined => {
        return rows?.slice().sort((a, b) => dayjs(a.startDate).isAfter(dayjs(b.startDate)) ? -1 : 1);
    }
);

// Base selector for row details
const selectAllRowDetails = (state: StateWithAccountLinkingDashboard) => state.accountLinkingDashboard.rowDetails;

// Memoized parameterized selector for row details by executionId
const rowDetailsSelectorCache = new Map<string, (state: StateWithAccountLinkingDashboard) => StepFunctionExecutionDetails | undefined>();

export const selectRowDetails = (executionId: string) => {
    if (!rowDetailsSelectorCache.has(executionId)) {
        const memoizedSelector = createSelector(
            [selectAllRowDetails],
            (rowDetails): StepFunctionExecutionDetails | undefined => {
                return rowDetails?.find((x) => x.executionId == executionId);
            }
        );
        rowDetailsSelectorCache.set(executionId, memoizedSelector);
    }
    return rowDetailsSelectorCache.get(executionId)!;
};
